import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Alert,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";
import creditService from "../../services/creditService";

export default function LevelTestPage() {
    const navigate = useNavigate();
    const { pathId, levelId } = useParams();
    const { userProfile } = useAuth();
    const [path, setPath] = useState(null);
    const [level, setLevel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [testStarted, setTestStarted] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [openResultDialog, setOpenResultDialog] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const pathData = await learningService.getLearningPathById(pathId);
                setPath(pathData);

                const levelData = await learningService.getLevelById(pathId, levelId);
                setLevel(levelData);

                const canAttempt = await learningService.canAttemptLevel(
                    userProfile?.uid,
                    pathId,
                    levelId
                );

                if (!canAttempt.canAttempt) {
                    setTestResult({
                        error: true,
                        message: canAttempt.reason
                    });
                }
            } catch (error) {
                console.error("Error fetching test data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pathId, levelId, userProfile]);

    const handleAnswerChange = (questionIndex, optionIndex) => {
        setAnswers((prev) => ({
            ...prev,
            [questionIndex]: optionIndex
        }));
    };

    const handleSubmitTest = async () => {
        try {
            setSubmitting(true);

            const questions = level?.questions || [];
            let correctAnswers = 0;

            questions.forEach((question, index) => {
                if (answers[index] === question.correctAnswer) {
                    correctAnswers++;
                }
            });

            const score = (correctAnswers / questions.length) * 100;

            const result = await learningService.updateLevelAttempt(
                userProfile?.uid,
                pathId,
                levelId,
                score,
                level?.qualifyingScore
            );

            if (result.passed && result.creditsEarned > 0) {
                await creditService.addCreditToUser(
                    userProfile?.uid,
                    result.creditsEarned
                );
            }

            setTestResult({
                score: Math.round(score),
                correctAnswers,
                totalQuestions: questions.length,
                passed: result.passed,
                creditsEarned: result.creditsEarned,
                pathCompleted: result.pathCompleted
            });
            setOpenResultDialog(true);
        } catch (error) {
            console.error("Error submitting test:", error);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout title="Level Test">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    if (testResult?.error) {
        return (
            <AdminLayout title="Level Test">
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{testResult.message}</Alert>
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={() => navigate("/student/learning/paths")}
                    >
                        Back to Learning Paths
                    </Button>
                </Box>
            </AdminLayout>
        );
    }

    if (!testStarted) {
        return (
            <AdminLayout title="Level Test">
                <Box sx={{ p: 3 }}>
                    <Card sx={{ maxWidth: 600, mx: "auto" }}>
                        <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                {level?.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                {level?.description}
                            </Typography>

                            <Box sx={{ mb: 3, p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                                <Typography variant="subtitle2">
                                    <strong>Test Details:</strong>
                                </Typography>
                                <Typography variant="body2">
                                    • Questions: {level?.questions?.length || 0}
                                </Typography>
                                <Typography variant="body2">
                                    • Duration: {level?.duration}
                                </Typography>
                                <Typography variant="body2">
                                    • Passing Score: {level?.qualifyingScore}%
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => setTestStarted(true)}
                            >
                                Start Test
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </AdminLayout>
        );
    }

    const currentQuestion = level?.questions?.[currentQuestionIndex];
    const totalQuestions = level?.questions?.length || 0;
    const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

    return (
        <AdminLayout title="Level Test">
            <Box sx={{ p: 3 }}>
                <Card sx={{ maxWidth: 800, mx: "auto" }}>
                    <CardContent>
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="subtitle2">
                                    Question {currentQuestionIndex + 1} of {totalQuestions}
                                </Typography>
                                <Chip
                                    label={`${Math.round(progress)}%`}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                            <LinearProgress variant="determinate" value={progress} />
                        </Box>

                        {currentQuestion && (
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                    {currentQuestion.text}
                                </Typography>

                                <RadioGroup
                                    value={answers[currentQuestionIndex] !== undefined ? answers[currentQuestionIndex] : ""}
                                    onChange={(e) =>
                                        handleAnswerChange(currentQuestionIndex, parseInt(e.target.value))
                                    }
                                >
                                    {currentQuestion.options.map((option, index) => (
                                        <FormControlLabel
                                            key={index}
                                            value={index}
                                            control={<Radio />}
                                            label={option}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                "&:hover": { backgroundColor: "action.hover" }
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </Box>
                        )}

                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                                disabled={currentQuestionIndex === 0}
                            >
                                Previous
                            </Button>

                            {currentQuestionIndex < totalQuestions - 1 ? (
                                <Button
                                    variant="contained"
                                    onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleSubmitTest}
                                    disabled={submitting}
                                >
                                    {submitting ? <CircularProgress size={22} /> : "Submit Test"}
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Result Dialog */}
            <Dialog
                open={openResultDialog}
                onClose={() => setOpenResultDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Test Result</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, textAlign: "center" }}>
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 700,
                                color: testResult?.passed ? "success.main" : "error.main",
                                mb: 1
                            }}
                        >
                            {testResult?.score}%
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {testResult?.passed ? "✨ Level Cleared!" : "Try Again"}
                        </Typography>

                        <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1, mb: 2 }}>
                            <Typography variant="body2">
                                Correct Answers: {testResult?.correctAnswers}/{testResult?.totalQuestions}
                            </Typography>
                            {testResult?.creditsEarned > 0 && (
                                <Typography variant="body2" sx={{ color: "success.main", fontWeight: 700 }}>
                                    Credits Earned: +{testResult?.creditsEarned}
                                </Typography>
                            )}
                            {testResult?.pathCompleted && (
                                <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 700 }}>
                                    🎉 Path Completed!
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => navigate("/student/learning/paths")}
                    >
                        Back to Paths
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/student/learning/progress")}
                    >
                        View Progress
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminLayout>
    );
}
